import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, message } from 'antd';
import issueTrackerService from '../../services/issueTrackerServices';
import constants from '../../config/constants';

const BRAND_TEAL = '#4F8C92';

interface Props {
  visible: boolean;
  orgid?: string;
  searchFilter?: string;
  filterData?: any;
  groupIds?: string[] | null;
  sensorboxIds?: string[] | null;
  startDate?: string;
  endDate?: string;
  onClose: () => void;
}

function pickUserRoleFilterIds(filterBag: any) {
  return {
    consumer: filterBag?.consumer?.[0]?._id ? [filterBag.consumer[0]._id] : null,
    contractor: filterBag?.contractor?.[0]?._id ? [filterBag.contractor[0]._id] : null,
    resolved_by: filterBag?.service_resolved_by?.[0]?._id ? [filterBag.service_resolved_by[0]._id] : null,
  };
}

function apiErrorMessage(error: any) {
  const data = error?.response?.data;
  if (typeof data?.detail === 'string') return data.detail;
  if (data?.detail?.message) return data.detail.message;
  return data?.message || 'Failed to export tickets';
}

const IssueTrackerExportModal: React.FC<Props> = ({
  visible,
  orgid,
  searchFilter,
  filterData,
  groupIds,
  sensorboxIds,
  startDate,
  endDate,
  onClose,
}) => {
  const [count, setCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildPayload = useCallback(() => {
    const { consumer, contractor, resolved_by } = pickUserRoleFilterIds(filterData);
    return {
      orgid,
      search: searchFilter || undefined,
      status: filterData?.status?.length ? filterData.status : undefined,
      consumer,
      contractor,
      service_resolved_by: resolved_by,
      group_ids: groupIds,
      sensorbox_ids: sensorboxIds,
      asset_group_ids: filterData?.asset_group?.[0]?._id ? [filterData.asset_group[0]._id] : null,
      asset_ids: filterData?.sensorbox_group?.[0]?._id ? [filterData.sensorbox_group[0]._id] : null,
      startDate,
      endDate,
    };
  }, [endDate, filterData, groupIds, orgid, searchFilter, sensorboxIds, startDate]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    let cancelled = false;
    const loadCount = async () => {
      setCountLoading(true);
      try {
        const response = await issueTrackerService.previewIssueTrackerExport(buildPayload());
        if (!cancelled) setCount(response?.count ?? 0);
      } catch (error) {
        if (!cancelled) {
          setCount(0);
          message.error(apiErrorMessage(error));
        }
      } finally {
        if (!cancelled) setCountLoading(false);
      }
    };
    setCount(null);
    loadCount();

    return () => {
      cancelled = true;
    };
  }, [visible, buildPayload]);

  const handleConfirm = async () => {
    if (!count) {
      return;
    }
    setExporting(true);
    try {
      await issueTrackerService.exportIssueTracker(buildPayload());
      message.success(constants.message.report_send);
      onClose();
    } catch (error) {
      message.error(apiErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  const countLabel = countLoading || count === null
    ? 'Counting tickets…'
    : `${count} ticket${count === 1 ? '' : 's'} will be exported.`;

  return (
    <Modal
      title="Export Tickets"
      visible={visible}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button
          key="cancel"
          type="text"
          style={{ color: BRAND_TEAL, fontWeight: 600 }}
          onClick={onClose}
        >
          Cancel
        </Button>,
        <Button
          key="export"
          type="primary"
          className="border-0 font-weight-bold"
          style={{ backgroundColor: BRAND_TEAL, borderColor: BRAND_TEAL }}
          loading={exporting}
          disabled={countLoading || !count}
          onClick={handleConfirm}
        >
          Export
        </Button>,
      ]}
    >
      <p className="mb-0 font-weight-bold">{countLabel}</p>
    </Modal>
  );
};

export default IssueTrackerExportModal;